import {
  computeFinanceAlerts,
  type FinanceAlert,
  type FinanceAlertPrefs,
  type FinanceExpense,
  type FinanceInvoice,
  type FinanceRecurringExpense,
  type FinanceSupplier,
  type FinanceSupplierTransaction,
} from '@/lib/finance';

export interface FinanceAlertSource {
  invoices: FinanceInvoice[];
  suppliers: FinanceSupplier[];
  supplierTransactions: FinanceSupplierTransaction[];
  recurring: FinanceRecurringExpense[];
  accountBalances: Record<number, number>;
  credit?: {
    customers: { id: number; full_name: string; credit_limit?: number | null }[];
    balances: Record<number, number>;
  } | null;
  expenses: FinanceExpense[];
  supplierBalance: (supplierId: number) => number;
  alertPrefs: FinanceAlertPrefs;
}

const SEVERITY_ORDER = { danger: 0, warning: 1, info: 2 } as const;

/**
 * Construit la liste d'alertes financières à partir des vraies données du
 * marchand, puis la trie par gravité. Utilisé par la vue d'ensemble ET par
 * le guetteur de notifications (cloche + PWA) pour rester cohérent.
 */
export function buildFinanceAlerts(source: FinanceAlertSource, currency: string): FinanceAlert[] {
  const supplierBalances = source.suppliers.map((s) => {
    const txs = source.supplierTransactions.filter((t) => t.supplier_id === s.id && t.type === 'invoice' && t.due_date);
    const dueDate = txs.length > 0 ? txs.map((t) => t.due_date as string).sort()[0] : null;
    return { name: s.name, balance: Math.max(0, source.supplierBalance(s.id)), dueDate };
  });

  const creditOverdue = (source.credit?.customers || [])
    .map((c) => ({ name: c.full_name, balance: source.credit?.balances?.[c.id] || 0, limit: c.credit_limit }))
    .filter((c) => c.balance > 0);

  return computeFinanceAlerts(
    {
      invoices: source.invoices,
      supplierBalances,
      recurring: source.recurring,
      accountBalances: Object.values(source.accountBalances),
      creditOverdue,
      expenses: source.expenses,
      currency,
    },
    source.alertPrefs,
  ).sort((a, b) => SEVERITY_ORDER[a.severity] - SEVERITY_ORDER[b.severity]);
}